import { useEffect, useState } from 'react';
import { canvasJsonToModel, type CanvasJSON, type PieceModel } from '@doer/frontend-components/creativity-editor';
import { buildScenesModel } from '../utils/build-scenes-model';
import { addCacheBusting } from '../../../utils/cache-busting';
import { hydrateSceneUrls } from '../utils/scene-url-utils';
import { smartMergePiece } from '../utils/smart-merge-piece';
import type { AvatarsProject, AvatarsScene } from './use-avatars-project';
import type { AvatarsSubtitleStyle } from '../types/subtitles';

type Options = {
  project: AvatarsProject;
  /** False while the closing card or the music track are still being resolved. */
  inputsReady: boolean;
  outro?: { videoUrl: string; durationMs: number };
  music?: { trackId: string; trackName: string; audioUrl: string };
  subtitleStyle?: AvatarsSubtitleStyle;
};

/** Mapa scene-id → URLs vigentes para pasarle al hidratador. */
const buildFreshUrlMap = (scenes: AvatarsScene[]) =>
  new Map(scenes.map(s => [s.sceneId, { videoUrl: s.videoUrl, audioUrl: s.audioUrl }]));

/**
 * The piece the editor opens: what the user saved, or a fresh compile of the scenes.
 * Projects saved before the model was persisted still carry a canvas JSON, so they get converted.
 *
 * Waits for every input: the editor opens the piece once, so a piece compiled before the music
 * lands would be the one it keeps.
 */
export const useAvatarsPieceModel = ({ project, inputsReady, outro, music, subtitleStyle }: Options) => {
  const [model, setModel] = useState<PieceModel | null>(null);

  useEffect(() => {
    if (!inputsReady) return;
    let cancelled = false;

    const resolve = async (): Promise<PieceModel> => {
      if (project.editorModel) {
        // Piece guardado en S3 → smart merge contra el estado actual de scenes:
        // regenera scene-derived (URLs, subs, outro, música), preserva user-added
        // (sobreimpresos, uploads propios). Cubre add/delete/modify de scenes sin
        // perder customizaciones del canvas.
        const url = addCacheBusting(project.editorModel.url, project.updatedAt ?? new Date());
        const response = await fetch(url);
        const s3Piece = (await response.json()) as PieceModel;
        return smartMergePiece(s3Piece, {
          projectId: project.projectId,
          scenes: project.scenes,
          dimension: project.dimension,
          outro,
          music,
          subtitleStyle
        });
      }
      if (project.editorState) {
        // Path legacy: proyectos guardados antes de que el piece viviera en S3.
        // Convertimos el canvasJSON al schema del piece y solo hidratamos URLs;
        // no aplicamos smart merge acá para no romper compatibilidad con casos raros.
        const piece = await canvasJsonToModel(project.editorState as CanvasJSON, {
          width: project.dimension.width,
          height: project.dimension.height,
          type: 'video'
        });
        return hydrateSceneUrls(piece, buildFreshUrlMap(project.scenes));
      }
      return buildScenesModel({
        projectId: project.projectId,
        scenes: project.scenes,
        dimension: project.dimension,
        outro,
        music,
        subtitleStyle
      });
    };

    resolve().then(resolved => {
      if (!cancelled) setModel(resolved);
    });

    return () => {
      cancelled = true;
    };
  }, [project, inputsReady, outro, music, subtitleStyle]);

  return model;
};
